import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Search, RefreshCw, Loader2, X, Check, Upload, Trash2 } from "lucide-react";

export function NfeHistoryModal({
  open,
  onOpenChange,
  // State
  search,
  setSearch,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  nfeHistory,
  loading,
  syncingNfes,
  // Actions
  onSearch,
  onSyncFromPg,
  onLoadNfeFromHistory,
  onDeleteNfe,
}) {
  const hasFilters = search || dateFrom || dateTo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="nfe-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <FileText className="w-5 h-5 text-[#4A90E2]" />
            Histórico de NF-e
          </DialogTitle>
          <DialogDescription>NF-e importadas para a empresa selecionada</DialogDescription>
        </DialogHeader>

        {/* Search and Filter Bar */}
        <div className="space-y-3 py-3 border-b border-slate-200">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Pesquisar por fornecedor, CNPJ ou número..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#4A90E2]/20 focus:border-[#4A90E2] outline-none"
              />
            </div>
            <Button
              size="sm"
              onClick={() => onSearch()}
              className="bg-[#4A90E2] hover:bg-[#3A80D2] text-white"
            >
              <Search className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              onClick={onSyncFromPg}
              disabled={syncingNfes}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              title="Sincronizar NF-es do sistema (últimos 30 dias)"
            >
              {syncingNfes ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-xs text-slate-500">Período:</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-[#4A90E2]/20 focus:border-[#4A90E2] outline-none"
            />
            <span className="text-xs text-slate-400">até</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-[#4A90E2]/20 focus:border-[#4A90E2] outline-none"
            />
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setDateFrom("");
                  setDateTo("");
                  onSearch({ search: "", dateFrom: "", dateTo: "" });
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="w-4 h-4 mr-1" />Limpar
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#4A90E2]" />
            </div>
          ) : nfeHistory.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">
                {hasFilters
                  ? "Nenhuma NF-e encontrada com os filtros aplicados"
                  : "Nenhuma NF-e importada ainda"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {nfeHistory.map((nfe) => (
                <div
                  key={nfe.id}
                  className={`p-4 rounded-xl border bg-white hover:shadow-md transition-shadow group ${
                    nfe.pending_products > 0
                      ? 'border-amber-300 bg-amber-50/50'
                      : nfe.all_imported
                        ? 'border-emerald-300 bg-emerald-50/50'
                        : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">NF-e #{nfe.nfe_number}</p>
                        {nfe.pending_products > 0 ? (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">
                            {nfe.pending_products} pendente{nfe.pending_products > 1 ? 's' : ''}
                          </Badge>
                        ) : nfe.all_imported ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">
                            <Check className="w-2.5 h-2.5 mr-0.5" />Importada
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-sm text-slate-600 truncate">{nfe.supplier_name}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span>CNPJ: {nfe.supplier_cnpj}</span>
                        <span>Data: {nfe.issue_date}</span>
                        <span className={nfe.pending_products > 0 ? 'text-amber-600 font-medium' : ''}>
                          {nfe.imported_products || 0}/{nfe.total_products || nfe.products?.length || 0} produtos
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#4A90E2]">R$ {nfe.total_value?.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">
                        Importada: {new Date(nfe.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      onClick={() => onLoadNfeFromHistory(nfe.id)}
                      className={nfe.pending_products > 0
                        ? "bg-amber-600 hover:bg-amber-700 text-white"
                        : "bg-[#4A90E2] hover:bg-[#3A80D2] text-white"
                      }
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      {nfe.pending_products > 0 ? 'Completar Importação' : 'Carregar'}
                    </Button>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm("Excluir apenas a NF-e (manter estoque)?")) {
                            onDeleteNfe(nfe.id, false);
                          }
                        }}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        title="Excluir apenas a NF-e"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm("Excluir NF-e E remover os lotes do estoque?")) {
                            onDeleteNfe(nfe.id, true);
                          }
                        }}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        title="Excluir NF-e e estoque"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />+ Estoque
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
